import _ from 'lodash';

import { companySorts } from '../../app/constants/sorts';
import { filterObject, KEY_ALIAS } from '../../app/constants/url';
import { routes } from '../../app/routes/routesConfig';

function isEmptyValue(value) {
  return (
    _.isNil(value) ||
    (typeof value === 'string' && !value.trim()) ||
    (Array.isArray(value) && !value.length) ||
    (_.isPlainObject(value) && _.isEmpty(value))
  );
}

function mapAliasKey(key) {
  return KEY_ALIAS[key] || key;
}

function filterZipArray(arrayValues) {
  const wildcardZips = arrayValues.filter((zipEntry) => typeof zipEntry === 'string' && zipEntry.endsWith('**'));
  const resultSet = new Set(arrayValues);
  wildcardZips.forEach((wildcard) => {
    const prefix = wildcard.replace('**', '');
    arrayValues.forEach(
      (zipCandidate) => zipCandidate !== wildcard && zipCandidate.startsWith(prefix) && resultSet.delete(zipCandidate)
    );
  });
  return [...resultSet];
}

function buildRateSegment(rateObject) {
  const items = Object.entries(rateObject)
    .filter(([, value]) => Array.isArray(value) && value.length === 2)
    .map(([rateKey, [minValue, maxValue]]) => `${rateKey}=${minValue}-${maxValue}`);
  return items.length ? `rate:${items.join('&')}` : '';
}

function buildArraySegment(keyName, arrayValues) {
  const finalList = keyName === 'zip' ? filterZipArray(arrayValues) : arrayValues;
  const dictionary = _.get(filterObject, keyName, {});
  return finalList.map((val) => dictionary[val] || val).join(',');
}

function buildSortSection(params, sort) {
  if (sort === companySorts.NAME.value) return `sort:${filterObject.sort[companySorts.NAME.value]}`;
  if (_.isEmpty(params)) return '';
  const outputSegments = [];

  if (typeof params.sort === 'string') {
    const rawSortValue = params.sort.trim();
    outputSegments.push(_.get(filterObject.sort, rawSortValue, ''));
  } else if (_.isPlainObject(params.sort) && !_.isEmpty(params.sort)) {
    const nested = [];
    for (const [key, value] of Object.entries(params.sort)) {
      if (key === 'rate' && _.isPlainObject(value) && !_.isEmpty(value)) {
        const rateString = buildRateSegment(value);
        if (rateString) nested.push(rateString);
      } else if (!isEmptyValue(value)) {
        const realKey = mapAliasKey(key);
        if (Array.isArray(value)) {
          const joined = buildArraySegment(realKey, value);
          if (joined) nested.push(`${realKey}=${joined}`);
        } else if (value === true && ['basis', 'offer', 'tax'].includes(realKey)) {
          nested.push(`${realKey}=true`);
        } else {
          nested.push(`${realKey}=${_.get(filterObject[realKey], value, value)}`);
        }
      }
    }
    if (nested.length) outputSegments.push(nested.join(';'));
  }

  for (const [key, paramValue] of Object.entries(params)) {
    if (key === 'sort') continue;
    if (key === 'rate' && _.isPlainObject(paramValue) && !_.isEmpty(paramValue)) {
      const rateSegment = buildRateSegment(paramValue);
      if (rateSegment) outputSegments.push(rateSegment);
    } else if (!isEmptyValue(paramValue) && key !== 'rate') {
      const realAliasKey = mapAliasKey(key);
      if (Array.isArray(paramValue)) {
        const arraySegment = buildArraySegment(realAliasKey, paramValue);
        if (arraySegment) outputSegments.push(`${realAliasKey}=${arraySegment}`);
      } else if (paramValue === true && ['basis', 'offer', 'tax'].includes(realAliasKey)) {
        outputSegments.push(`${realAliasKey}=true`);
      } else {
        outputSegments.push(`${realAliasKey}=${_.get(filterObject[realAliasKey], paramValue, paramValue)}`);
      }
    }
  }

  const final = outputSegments.filter(Boolean).join(';');
  return final ? `sort:${final}` : '';
}

function buildFilterSection(params) {
  if (_.isEmpty(params)) return '';
  const segments = [];

  for (const [key, paramValue] of Object.entries(params)) {
    if (key === 'rate' && _.isPlainObject(paramValue) && !_.isEmpty(paramValue)) {
      const rateBlock = buildRateSegment(paramValue);
      if (rateBlock) segments.push(rateBlock);
    } else if (!isEmptyValue(paramValue)) {
      const realKey = mapAliasKey(key);
      if (Array.isArray(paramValue)) {
        const filtered = paramValue.filter((item) => !isEmptyValue(item));
        if (!filtered.length) continue;
        const arrayString = buildArraySegment(realKey, filtered);
        if (arrayString) segments.push(`${realKey}=${arrayString}`);
      } else if (paramValue === true && ['basis', 'offer', 'tax'].includes(realKey)) {
        segments.push(`${realKey}=true`);
      } else {
        segments.push(`${realKey}=${_.get(filterObject[realKey], paramValue, paramValue)}`);
      }
    }
  }

  const output = segments.filter(Boolean).join(';');
  return output ? `filter:${output}` : '';
}

function buildInverseDictionary() {
  const aliasInverted = {};
  for (const [reduxKey, shortKey] of Object.entries(KEY_ALIAS)) {
    aliasInverted[shortKey] = reduxKey;
  }

  const invertedFilterObject = {};
  for (const topLevelKey of Object.keys(filterObject)) {
    const subObject = filterObject[topLevelKey];
    if (_.isPlainObject(subObject)) {
      const tempObj = {};
      for (const [longValue, shortValue] of Object.entries(subObject)) {
        tempObj[shortValue] = longValue;
      }
      invertedFilterObject[topLevelKey] = tempObj;
    } else {
      invertedFilterObject[topLevelKey] = subObject;
    }
  }

  return { aliasInverted, invertedFilterObject };
}

const { aliasInverted, invertedFilterObject } = buildInverseDictionary();

function fallbackShortValue(keyName, val) {
  const dictionary = invertedFilterObject[keyName];
  if (!dictionary) return val;
  return Object.prototype.hasOwnProperty.call(dictionary, val) ? dictionary[val] : val;
}

function convertZip(zipVal) {
  const found = fallbackShortValue('zip', zipVal);
  if (!found || !found.endsWith || !found.endsWith('**')) return found ? [found] : [];
  const prefix = found.replace('**', '');
  const output = [found];
  const from = parseInt(prefix + '00', 10);
  const to = parseInt(prefix + '46', 10);
  for (let i = from; i <= to; i++) {
    output.push(String(i));
  }
  return output;
}

function parseRateSegment(piece, resultObject) {
  if (!resultObject.rate) resultObject.rate = {};
  const afterRate = piece.slice('rate:'.length).trim();
  afterRate
    .split('&')
    .map((ratePair) => ratePair.trim())
    .filter(Boolean)
    .forEach((pair) => {
      const [key, range] = pair.split('=');
      if (!key || !range) return;
      const [minValue, maxValue] = range.split('-');
      resultObject.rate[key] = [Number(minValue), Number(maxValue)];
    });
}

function parseKeyValueSegment(segment, resultObject) {
  const [key, val] = segment.split('=');
  if (!key || !val) return;

  if (key === 'search') {
    resultObject.search = val;
    return;
  }

  const reduxKey = aliasInverted[key];
  if (!reduxKey) return;

  if (val === 'true' && ['basis', 'offer', 'tax'].includes(key)) {
    resultObject[reduxKey] = true;
    return;
  }

  if (val.includes(',')) {
    const splitted = val.split(',').filter(Boolean);
    if (key === 'zip') {
      const expanded = splitted.flatMap(convertZip).filter(Boolean);
      resultObject[reduxKey] = expanded.length
        ? expanded
        : splitted.map((zipValue) => fallbackShortValue(key, zipValue));
      return;
    }
    resultObject[reduxKey] = splitted.map((vv) => fallbackShortValue(key, vv));
    return;
  }

  if (key === 'zip') {
    const expandedZip = convertZip(val);
    if (expandedZip.length) {
      resultObject[reduxKey] = expandedZip;
    } else {
      const singleVal = fallbackShortValue(key, val);
      if (singleVal) resultObject[reduxKey] = [singleVal];
    }
    return;
  }

  const singleValue = fallbackShortValue(key, val);
  if (singleValue) resultObject[reduxKey] = singleValue;
}

function parseBlock(blockString) {
  if (!blockString || !blockString.trim()) return {};

  return blockString
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .reduce((accumulator, segment) => {
      if (segment.startsWith('rate:')) {
        parseRateSegment(segment, accumulator);
      } else {
        parseKeyValueSegment(segment, accumulator);
      }
      return accumulator;
    }, {});
}

export function buildSortFilterUrl({ sortParams, filterParams, sort }) {
  const sortSection = buildSortSection(sortParams, sort);
  const filterSection = buildFilterSection(filterParams);
  const resultSections = [];

  if (!_.isEmpty(sortSection)) resultSections.push(sortSection);
  if (!_.isEmpty(filterSection)) resultSections.push(filterSection);

  return resultSections.length ? `${routes.COMPANIES}?/${resultSections.join(';')}` : routes.COMPANIES;
}

export function parseSortFilterUrl(fullUrl) {
  let paramString = '';
  const questionIndex = fullUrl.indexOf('?');
  if (questionIndex !== -1) {
    paramString = fullUrl.slice(questionIndex + 1);
  } else {
    const slashIndex = fullUrl.indexOf('/sort:');
    if (slashIndex !== -1) paramString = fullUrl.slice(slashIndex);
  }
  if (paramString.startsWith('/')) paramString = paramString.slice(1);

  const filterIndex = paramString.indexOf('filter:');
  const sortBlock = filterIndex !== -1 ? paramString.slice(0, filterIndex) : paramString;
  const filterBlock = filterIndex !== -1 ? paramString.slice(filterIndex).replace(/^filter:/, '') : '';

  const finalSortString = sortBlock.replace(/^sort:/, '');
  const filterParams = parseBlock(filterBlock);

  return {
    filterParams,
    ...(finalSortString.includes(filterObject.sort[companySorts.NAME.value])
      ? { sort: companySorts.NAME.value }
      : { sortParams: parseBlock(finalSortString) }),
  };
}
